import {useId, useRef, type FocusEvent, type KeyboardEvent, type MouseEvent} from 'react'
import {clsx} from 'clsx'

import {ChevronRightIcon} from '@primer/octicons-react'

import {NavGroup, type NavGroupType} from '../NavGroup/NavGroup'
import {NavLink} from '../NavLink/NavLink'
import type {NavLinkType} from '../NavLink/NavLink'

import {useDropdownPositioning} from './use-dropdown-positioning'

import styles from './NavDropdown.module.css'

export type NavDropdownType = {
  groups: NavGroupType[]
  render?: boolean
  title: string
  trailingLink?: NavLinkType
}

type NavDropdownProps = NavDropdownType & {
  isOpen?: boolean
  onOpenChange?: (nextOpen: boolean) => void
}

// Desktop hover-open is purely CSS and intentionally separate from React state. Click/keyboard
// dismissal (mouseleave, Escape, focus-out) only applies to hover-capable fine pointers at the
// desktop breakpoint, so it never fires on touch or on the narrow inline-accordion layout (below
// 1012px), where the dropdown behaves as an accordion rather than a hover panel.
function isHoverCapableDesktop(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 1012px)').matches
  )
}

export function NavDropdown(props: NavDropdownProps) {
  const {title, groups, render = true, trailingLink, isOpen = false, onOpenChange} = props

  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const panelId = useId()

  useDropdownPositioning(containerRef, dropdownRef, isOpen)

  function handleButtonClick(event: MouseEvent<HTMLButtonElement>) {
    // Ignore non-primary (middle/right) activations.
    if (event.button !== 0) return
    onOpenChange?.(!isOpen)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    // Keep Escape local: stop it before it reaches the marketing-header's document-level Escape
    // handler, so the first Escape closes only this panel and a second closes the header.
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.key !== 'Escape' || !isOpen) return
    event.preventDefault()
    event.stopPropagation()
    onOpenChange?.(false)
    buttonRef.current?.focus()
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (!isOpen) return
    const nextFocused = event.relatedTarget as Node | null
    // Focus stayed inside the disclosure — not a dismissal.
    if (nextFocused && event.currentTarget.contains(nextFocused)) return
    onOpenChange?.(false)
  }

  function handleMouseLeave() {
    if (!isOpen || !isHoverCapableDesktop()) return
    // If focus is currently inside the panel we're about to hide, move it back to the trigger so
    // it's never trapped in hidden content.
    const active = document.activeElement
    const shouldRestoreFocus = active instanceof HTMLElement && Boolean(containerRef.current?.contains(active))
    onOpenChange?.(false)
    if (shouldRestoreFocus) buttonRef.current?.focus()
  }

  if (render === false) {
    return null
  }

  return (
    // The button is the interactive control; these container-level handlers only add dismissal
    // affordances (Escape, focus-out, mouse-leave) for the disclosure and don't make the wrapper a
    // widget, so it intentionally has no role.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={containerRef}
      className={clsx(styles.container, {
        // Global `open` class drives the CSS open state; derived from React state instead of the
        // legacy dotcom details global behavior.
        // eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals
        open: isOpen,
      })}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onMouseLeave={handleMouseLeave}
    >
      <button
        ref={buttonRef}
        type="button"
        className={clsx(styles.button)}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={handleButtonClick}
      >
        {title}

        <ChevronRightIcon className={clsx(styles.buttonIcon)} />
      </button>

      <div ref={dropdownRef} id={panelId} className={clsx(styles.dropdown)}>
        <ul className={styles.list}>
          {groups.map(group => (
            <li key={group.title}>
              <NavGroup {...group} context={title} />
            </li>
          ))}
        </ul>

        {trailingLink ? (
          <div className={styles.trailingLinkContainer}>
            <NavLink {...trailingLink} context={title} withArrow />
          </div>
        ) : null}
      </div>
    </div>
  )
}
